
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Draggable } from '@hello-pangea/dnd';
import type { Candidate } from "./types";
import { useState } from "react";
import CandidateProfileComponent from "./CandidateProfile";
import { CandidateInfo } from "./pipeline/CandidateInfo";
import { StatusActions } from "./pipeline/StatusActions";
import { MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteButton } from "@/components/common/DeleteButton";
import { useNavigate } from "react-router-dom";

interface PipelineCardProps {
  candidate: Candidate;
  index: number;
  status?: string;
  onMarkAsHiredRequest?: (candidate: Candidate) => void;
}

export const PipelineCard = ({ 
  candidate, 
  index,
  status, 
  onMarkAsHiredRequest
}: PipelineCardProps) => {
  const [showProfile, setShowProfile] = useState(false);
  const navigate = useNavigate();

  const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown Candidate';

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open profile if clicking on dropdown or buttons
    if ((e.target as HTMLElement).closest('[data-radix-popper-content-wrapper]') || 
        (e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (!showProfile) {
      setShowProfile(true);
    }
  };

  return (
    <Draggable draggableId={candidate.id} index={index}>
      {(provided, snapshot) => (
        <Card 
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`group p-4 hover:bg-accent transition-colors cursor-pointer border border-border hover:border-primary/20 hover:shadow-md transition-all relative ${
            snapshot.isDragging ? 'shadow-lg rotate-2' : ''
          }`}
          onClick={handleCardClick}
        >
          {/* Context Menu Button */}
          <div className="absolute top-2 right-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/candidates/${candidate.id}`)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <DeleteButton
                    entityType="candidate"
                    entityId={candidate.id}
                    entityName={candidateName}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-destructive hover:text-destructive"
                  >
                    Delete Candidate
                  </DeleteButton>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-start gap-3">
            <CandidateInfo candidate={candidate} />
          </div>

          <StatusActions 
            status={status} 
            candidate={candidate} 
            onMarkAsHiredRequest={onMarkAsHiredRequest}
          />

          <CandidateProfileComponent 
            candidateId={candidate.id} 
            open={showProfile} 
            onOpenChange={setShowProfile} 
          />
        </Card>
      )}
    </Draggable>
  );
};
